/**
 * The account, in the corner of the header.
 *
 * The avatar is a button with a chevron so it reads as a menu before it is
 * opened. The menu names the signed-in account and holds the three account
 * actions: Profile, Settings, Log out. It closes on Escape, on an outside
 * press, and on whatever it opens.
 */

import { useEffect, useRef, useState } from 'react';
import type { User } from '../api/types.js';

export type AccountDestination = 'profile' | 'settings';

export interface UserMenuProps {
  user: User;
  onNavigate: (destination: AccountDestination) => void;
  onSignOut: () => void;
}

export function UserMenu({ user, onNavigate, onSignOut }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent): void => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open ]);

  const go = (destination: AccountDestination): void => {
    setOpen(false);
    onNavigate(destination);
  };

  return (
    <div className="user-menu" ref={root}>
      <button
        type="button"
        className="user-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${user.displayName}`}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="avatar" aria-hidden="true">
          {(user.displayName || user.email || 'U').slice(0, 1).toUpperCase()}
        </span>
        <span className="user-name">{user.displayName}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="menu" role="menu" aria-label="Account">
          <div className="menu-head">
            <strong>{user.displayName}</strong>
            <p className="hint">{user.email}</p>
          </div>
          <button type="button" role="menuitem" onClick={() => go('profile')}>
            Profile
          </button>
          <button type="button" role="menuitem" onClick={() => go('settings')}>
            Settings
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
          >
            Log out
          </button>
        </div>
      )}
    </div>
  );
}
